import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DefaultProfileSections } from "@/components/users/user-details/default-profile-sections";
import { Header } from "@/components/users/user-details/header.tsx";
import { Measurements } from "@/components/users/user-details/measurements";
import { AddMentorship } from "@/components/users/user-details/mentorships/add-mentorship.tsx";
import { Mentorships } from "@/components/users/user-details/mentorships/mentorships.tsx";
import { OnboardingMessagesSection } from "@/components/users/user-details/onboarding-messages-section";
import { ProfileSectionsManager } from "@/components/users/user-details/profile-sections/profile-sections-manager";
import { RolesTab } from "@/components/users/user-details/roles-tab";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { requirePrivilegedAccess } from "@/lib/casl/route-guards";
import { SupervisorMentors } from "@/components/users/user-details/supervisor-mentors/supervisor-mentors";
import { UserDetails } from "@/components/users/user-details/user-details.tsx";
import { UserAccessControl } from "@/components/users/user-details/user-access-control";
import { UserExtraDetails } from "@/components/users/user-details/user-extra-details.tsx";
import { UserMentorProfile } from "@/components/users/user-details/user-mentor-profile";
import { UserMentorSchedule } from "@/components/users/user-details/user-mentor-schedule";
import { UserMentorSlots } from "@/components/users/user-details/user-mentor-slots";
import { UserParentDetails } from "@/components/users/user-details/user-parent-details.tsx";
import { useGetUserMentorships } from "@/lib/api/mentorships.ts";
import { useGetUserSupervisorMentors, useUser } from "@/lib/api/user.ts";
import { isMenteeIdentity, isMentorIdentity } from "@/lib/utils/system-roles";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useMemo } from "react";
import { MentorStoriesManager } from "@/components/users/user-details/mentor-stories/mentor-stories-manager";

export const Route = createFileRoute("/_authenticated/users/$userId")({
  component: UserDetailsPage,
  beforeLoad: ({ context }) => {
    requirePrivilegedAccess(context.user);
  },
});

function UserDetailsPage() {
  const { userId } = useParams({
    strict: false,
  }) as { userId: string };

  const { data: user, isPending, isError } = useUser(userId);
  const ability = useAbility();
  const hasPrivilegedAccess = hasPrivilegedAccessFromAbility(ability);
  const canViewRoles = hasPrivilegedAccess && ability.can("read", "roles");
  const canReadMentorships =
    hasPrivilegedAccess && ability.can("read", "mentorships");
  const canReadMentorSlots =
    hasPrivilegedAccess && ability.can("read", "mentor-slots");
  const canAssignSupervisor =
    hasPrivilegedAccess && ability.can("assign", "supervisor");
  const canManageMentorStories =
    hasPrivilegedAccess && ability.can("update", "users.profile-media");
  const canUnassignSupervisor =
    hasPrivilegedAccess && ability.can("unassign", "supervisor");
  const canManageSupervisorAssignments =
    canAssignSupervisor || canUnassignSupervisor;

  const hasMentorRole = isMentorIdentity(user?.systemRoles);
  const hasMenteeRole = isMenteeIdentity(user?.systemRoles);

  const { data: mentorships, isPending: isMentorshipsPending } =
    useGetUserMentorships(userId, canReadMentorships);
  const { data: supervisorMentors, isPending: isSupervisorMentorsPending } =
    useGetUserSupervisorMentors(userId, canReadMentorships);
  const hasSupervisedMentors = (supervisorMentors?.mentors?.length ?? 0) > 0;

  const canShowSupervisionTab =
    canReadMentorships &&
    !hasMenteeRole &&
    (hasSupervisedMentors || canManageSupervisorAssignments);

  const showMentorshipsTab =
    canReadMentorships && (hasMentorRole || hasMenteeRole);

  const disableAddMentorship = useMemo(() => {
    if (!hasMenteeRole) {
      return false;
    }
    return mentorships?.some((mentorship) => !mentorship.deactivated);
  }, [hasMenteeRole, mentorships]);

  if (isPending) {
    return (
      <div className="h-screen-nav bg-primary-100 flex h-screen w-full items-center justify-center">
        <Loader2 className="text-blue-brand h-12 w-12 animate-spin" />
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="h-screen-nav bg-primary-100 flex h-screen w-full items-center justify-center">
        <div className="text-blue-brand">Some errors occurred!</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto space-y-6 px-4 py-6">
        <Header user={user} />

        <Tabs defaultValue="profile" className="w-full space-y-6">
          <TabsList className="w-full justify-start border-b pb-px">
            <TabsTrigger value="profile">Basic Profile</TabsTrigger>
            {hasMentorRole && (
              <TabsTrigger value="mentor-settings">Mentor Settings</TabsTrigger>
            )}

            {hasMentorRole && (
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
            )}

            {showMentorshipsTab && (
              <TabsTrigger value="mentorships">Mentorships</TabsTrigger>
            )}

            {canShowSupervisionTab && (
              <TabsTrigger value="supervision">Supervision</TabsTrigger>
            )}

            {hasMentorRole && (
              <TabsTrigger value="onboarding-messages">
                Onboarding Messages
              </TabsTrigger>
            )}

            {hasMentorRole && (
              <TabsTrigger value="profile-sections">
                Matching Profile
              </TabsTrigger>
            )}

            {canViewRoles && (
              <TabsTrigger value="roles">Roles & Permissions</TabsTrigger>
            )}
            {hasMentorRole && canManageMentorStories && (
              <TabsTrigger value="mentor-stories">
                Mentor Stories
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="profile" className="space-y-6">
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <Card className="space-y-8 p-6">
                <div>
                  <h2 className="mb-4 text-xl font-semibold">User Details</h2>
                  <UserDetails user={user} />
                </div>
                {hasMenteeRole && (
                  <div>
                    <h2 className="mb-4 text-xl font-semibold">
                      Parent Details
                    </h2>
                    <UserParentDetails user={user} />
                  </div>
                )}
              </Card>
              <div className="grid grid-cols-1 gap-6">
                {hasMenteeRole &&
                  user?.profile?.metadata?.researchConsent?.consent !==
                    true && (
                    <Card className="space-y-8 p-6">
                      <div>
                        <h2 className="mb-4 text-xl font-semibold">
                          Measurements
                        </h2>
                        <Measurements user={user} />
                      </div>
                    </Card>
                  )}
                <Card className="space-y-8 p-6">
                  <div>
                    <h2 className="mb-4 text-xl font-semibold">
                      Additional Information
                    </h2>
                    <UserExtraDetails user={user} />
                  </div>
                </Card>
                {hasMenteeRole && (
                  <Card className="space-y-8 p-6">
                    <div>
                      <h2 className="mb-4 text-xl font-semibold">
                        Access Control
                      </h2>
                      <UserAccessControl user={user} />
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {hasMentorRole && (
            <TabsContent value="mentor-settings" className="space-y-6">
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card className="space-y-8 p-6">
                  <div>
                    <h2 className="mb-4 text-xl font-semibold">
                      Mentor Profile
                    </h2>
                    <UserMentorProfile user={user} />
                  </div>
                </Card>
                {canReadMentorSlots && (
                  <Card className="space-y-8 p-6">
                    <div>
                      <h2 className="mb-4 text-xl font-semibold">
                        Availability
                      </h2>
                      <UserMentorSlots user={user} />
                    </div>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}

          {hasMentorRole && (
            <TabsContent value="schedule">
              <Card className="p-6">
                <UserMentorSchedule user={user} />
              </Card>
            </TabsContent>
          )}

          {showMentorshipsTab && (
            <TabsContent value="mentorships">
              <Card className="p-6">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Mentorships</h2>
                  <AddMentorship
                    userId={userId}
                    isMentor={hasMentorRole}
                    isMentee={hasMenteeRole}
                    disabled={disableAddMentorship}
                  />
                </div>
                <Mentorships
                  userId={userId}
                  isMentor={hasMentorRole}
                  isPending={isMentorshipsPending}
                  mentorships={mentorships || []}
                />
              </Card>
            </TabsContent>
          )}

          {canShowSupervisionTab && (
            <TabsContent value="supervision">
              <Card className="p-6">
                <h2 className="mb-6 text-xl font-semibold">
                  Supervised Mentors
                </h2>
                <SupervisorMentors
                  userId={userId}
                  isPending={isSupervisorMentorsPending}
                  mentors={supervisorMentors?.mentors || []}
                  canAssignSupervisor={canAssignSupervisor}
                  canUnassignSupervisor={canUnassignSupervisor}
                />
              </Card>
            </TabsContent>
          )}

          {hasMentorRole && (
            <TabsContent value="onboarding-messages">
              <OnboardingMessagesSection userId={userId} />
            </TabsContent>
          )}

          {hasMentorRole && (
            <TabsContent value="profile-sections">
              <Card className="mb-8 p-6">
                <DefaultProfileSections userId={userId} />
              </Card>
              <Card className="p-6">
                <ProfileSectionsManager userId={userId} />
              </Card>
            </TabsContent>
          )}

          {canViewRoles && (
            <TabsContent value="roles">
              <Card className="p-6">
                <RolesTab userId={userId} />
              </Card>
            </TabsContent>
          )}

          {hasMentorRole && canManageMentorStories && (
            <TabsContent value="mentor-stories">
              <Card className="p-6">
                <MentorStoriesManager mentorId={userId} />
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
